import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const { FiClock, FiCalendar, FiUser, FiPhone, FiMail } = FiIcons;

const BuffetMenuPage = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [formSubmissionStatus, setFormSubmissionStatus] = useState({ message: '', isError: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    number: '',
    email: '',
    guests: '',
    date: '',
    time: '',
    message: ''
  });

  const menuSections = [
    {
      title: 'STARTERS',
      items: [
        'Chicken Wings',
        'Chicken Strips',
        'Chicken Nuggets',
        'Kebab',
        'Spring Roll',
        'Veg Samosa',
        'Pakora',
        'Fish Fingers',
        'Sausages',
        'Chana Chaat',
        'Fries'
      ]
    },
    {
      title: 'MAIN COURSE',
      items: [
        'Chicken Tikka Masala',
        'Butter Chicken',
        'Lamb Balti',
        'Beef Burgers',
        'Veggie Burgers',
        'Chicken Burgers',
        'Veg Pasta',
        'Daal',
        'Biryani',
        'Vegetable Dish',
        'Spaghetti Bolognese',
        'Chicken Corn Soup',
        'Tacos'
      ]
    },
    {
      title: 'SIDES',
      items: [
        'Egg Fried Rice',
        'Boiled Rice',
        'Plain Naans',
        'Poppadoms',
        'Sauces',
        'Variety of Salad'
      ]
    },
    {
      title: 'DESSERTS',
      items: [
        'Rice Pudding',
        'Chocolate Cake',
        'Custard',
        'Ice Cream (Strawberry, Vanilla & Chocolate)'
      ]
    },
    {
      title: 'DRINKS',
      items: [
        'Unlimited Soft Drinks – £4 per person',
        'Bottled Water – £1.00',
        'Tea & Coffee – £2.00',
        '❌ Outside soft drinks are not permitted.'
      ]
    }
  ];

  const timeSlots = [
    '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00', '20:30', '21:00'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openBookingModal = () => {
    setIsBookingModalOpen(true);
    setIsSubmitting(false);
    setIsSubmitted(false);
    setFormSubmissionStatus({ message: '', isError: false });
  };

  const closeBookingModal = () => {
    setIsBookingModalOpen(false);
    setIsSubmitting(false);
    setIsSubmitted(false);
    setFormSubmissionStatus({ message: '', isError: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setIsSubmitted(false);
    
    const scriptURL = 'https://script.google.com/macros/s/AKfycbyoNo9BWWrXAN8qpZeNSyr-AfXZRJcW0q_zOo47g1w6zUXSocmdPIQOXxniWozdo2YDfQ/exec';

    // Prepare payload
    const payload = {
      name: formData.name,
      number: formData.number,
      email: formData.email,
      guests: formData.guests,
      date: formData.date,
      time: formData.time,
      message: formData.message
    };

    // Try JSON POST first
    try {
      console.log('Submitting to Google Script (JSON):', payload);
      const res = await fetch(scriptURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        mode: 'no-cors'
      });

      // With no-cors mode, we can't read the response but submission happens
      console.log('Form submitted successfully');
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormSubmissionStatus({ message: 'Booking request submitted! We will contact you shortly.', isError: false });
      
      // Keep the button green for 2 seconds before closing
      setTimeout(() => {
        setIsBookingModalOpen(false);
        setIsSubmitted(false);
        setFormData({ name: '', number: '', email: '', guests: '', date: '', time: '', message: '' });
      }, 2000);
      return;
    } catch (err) {
      console.warn('JSON POST error, trying fallback:', err);
    }

    // Fallback: create a hidden iframe + form and submit it (bypasses CORS preflight)
    try {
      console.log('Submitting to Google Script via hidden iframe (fallback)');

      const submitViaIframe = (payload) => {
        return new Promise((resolve, reject) => {
          const iframeName = 'gs_iframe_' + Date.now();
          const iframe = document.createElement('iframe');
          iframe.name = iframeName;
          iframe.style.display = 'none';
          document.body.appendChild(iframe);

          const formEl = document.createElement('form');
          formEl.action = scriptURL;
          formEl.method = 'POST';
          formEl.target = iframeName;

          Object.entries(payload).forEach(([k, v]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = k;
            input.value = v;
            formEl.appendChild(input);
          });

          formEl.style.display = 'none';
          document.body.appendChild(formEl);

          // Resolve after iframe loads or after timeout
          const cleanup = () => {
            setTimeout(() => {
              try { iframe.remove(); } catch (e) { /* ignore */ }
              try { formEl.remove(); } catch (e) { /* ignore */ }
            }, 500);
          };

          iframe.onload = () => {
            cleanup();
            resolve();
          };

          try {
            formEl.submit();
          } catch (e) {
            // submission might still happen; wait a short time
            console.warn('iframe submit error (continuing):', e);
          }

          // Fallback resolve in case onload doesn't fire (some browsers)
          setTimeout(() => {
            cleanup();
            resolve();
          }, 2500);
        });
      };

      await submitViaIframe(payload);

      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormSubmissionStatus({ message: 'Booking request submitted! We will contact you shortly.', isError: false });
      
      // Keep the button green for 2 seconds before closing
      setTimeout(() => {
        setIsBookingModalOpen(false);
        setIsSubmitted(false);
        setFormData({ name: '', number: '', email: '', guests: '', date: '', time: '', message: '' });
      }, 2000);
    } catch (err) {
      console.error('Fallback iframe submit failed:', err);
      setIsSubmitting(false);
      setFormSubmissionStatus({ message: 'Form submit nahi hua. Console mein error dekhein (F12) aur Google Script deployment settings check karein.', isError: true });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50"
    >
      {/* Header Section */}
      <div className="relative bg-gradient-to-r from-primary to-purple-800 py-20 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center text-white max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <div className="mb-10 mt-16">
                <h1 className="font-display text-4xl md:text-6xl font-bold mb-4 tracking-wide">
                  Castle Hotel
                </h1>
                <h2 className="font-display text-2xl md:text-5xl font-bold mb-8 text-yellow-300 tracking-wide">
                  BUFFET MENU
                </h2>
              </div>
              
              {/* Special Offer Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 border border-white/30 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <div className="text-3xl mb-3">⏰</div>
                  <h3 className="text-yellow-300 font-bold text-lg mb-2">Timing</h3>
                  <p className="text-white/90 text-sm mt-2">Friday to Sunday</p>
                  <p className="text-white/90 text-sm">Friday - 5-10pm</p>
                  <p className="text-white/90 text-sm">Sat-Sun - 3-10pm</p>
                </div>
                
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 border border-white/30 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <div className="text-3xl mb-3">👶</div>
                  <h3 className="text-yellow-300 font-bold text-lg mb-2">Under 5 Eat Free</h3>
                  <p className="text-white/90 text-sm mt-2">Children (5-10 years) Half Price</p>
                  <p className="text-white/90 text-sm">All You Can Eat</p>
                  <p className="text-2xl font-bold mt-2">£19.99</p>
                </div>
                
                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 border border-white/30 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <div className="text-3xl mb-3">🎓</div>
                  <h3 className="text-red-300 font-bold text-lg mb-2">Special Discounts</h3>
                  <p className="text-white/90 text-sm mt-2">Students</p>
                  <p className="text-white/90 text-sm">Senior Citizen</p>
                  <p className="text-white/90 text-sm">NHS Staff</p>
                  <p className="text-2xl font-bold mt-2">15% OFF</p>
                </div>
              </div>
              
              <button
                onClick={openBookingModal}
                className="btn-primary bg-white text-primary px-8 py-4 rounded-full text-xl font-bold hover:bg-white/90 transition-all elevation-2 shadow-2xl transform hover:scale-105 duration-300"
              >
                Reserve Your Table Now
              </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Menu Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              *Castle Hotel - Buffet Meal Menu*
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {menuSections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <div className="bg-primary text-white py-4 px-6">
                  <h3 className="font-display text-xl font-bold">{section.title}</h3>
                </div>
                <div className="p-6">
                  <ul className="space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start">
                        <span className="text-primary mr-2">•</span>
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {isBookingModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="font-display text-2xl font-bold text-gray-900">
                Reserve Your Table
              </h2>
              <button
                onClick={closeBookingModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close booking modal"
              >
                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiUser} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Your full name"
                    />
                  </div>
                </div>

                {formSubmissionStatus.message && (
                  <div className={`mt-4 p-3 rounded-md ${formSubmissionStatus.isError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {formSubmissionStatus.message}
                  </div>
                )}

                <div>
                  <label htmlFor="number" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiPhone} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      id="number"
                      name="number"
                      value={formData.number}
                      onChange={handleInputChange}
                      required
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Your phone number"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiMail} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="guests" className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Guests *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiUser} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      id="guests"
                      name="guests"
                      value={formData.guests}
                      onChange={handleInputChange}
                      min="1"
                      max="50"
                      required
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Number of guests"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                    Date *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiCalendar} className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1">
                    Time *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SafeIcon icon={FiClock} className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      required
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary appearance-none"
                    >
                      <option value="">Select a time</option>
                      {timeSlots.map((time, index) => (
                        <option key={index} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="Any special requests or notes..."
                  ></textarea>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting || isSubmitted}
                    className={`w-full py-3 px-4 rounded-md font-medium transition-all duration-300 ${
                      isSubmitted 
                        ? 'bg-green-600 text-white cursor-not-allowed' 
                        : isSubmitting 
                        ? 'bg-gray-400 text-white cursor-not-allowed' 
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                  >
                    {isSubmitted ? '✓ Submitted' : isSubmitting ? 'Submitting...' : 'Submit Reservation'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default BuffetMenuPage;